package com.sweetlime.bingeamigo.presentation.features.library

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.bingeamigo.model.LibraryMovie
import com.sweetlime.bingeamigo.model.Movie
import com.sweetlime.bingeamigo.model.WatchMovie
import com.sweetlime.bingeamigo.presentation.components.LibraryTab
import com.sweetlime.bingeamigo.repository.LibraryRepository
import com.sweetlime.bingeamigo.repository.ReviewRepository
import com.sweetlime.bingeamigo.state.LibraryUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class LibraryViewModel(
    private val repository: LibraryRepository,
    private val reviewRepository: ReviewRepository,
) : ViewModel() {

    private val _selectedTab = MutableStateFlow(LibraryTab.WATCHLIST)
    val selectedTab: StateFlow<LibraryTab> = _selectedTab.asStateFlow()

    // --- Raw lists ---
    val watchlist: StateFlow<List<LibraryMovie>> = repository.getWatchlist()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val watched: StateFlow<List<LibraryMovie>> = repository.getWatched()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val favorites: StateFlow<List<LibraryMovie>> = repository.getFavorites()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    // --- Counts for segment badges ---
    val counts: StateFlow<Map<LibraryTab, Int>> = combine(
        watchlist, watched, favorites
    ) { watchlist, watched, favorites ->
        mapOf(
            LibraryTab.WATCHLIST  to watchlist.size,
            LibraryTab.WATCHED    to watched.size,
            LibraryTab.FAVORITES  to favorites.size
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyMap())

    // --- Community ratings (MOVIE_id → avgRating) ---
    private val _communityRatings = MutableStateFlow<Map<Int, Double>>(emptyMap())
    val communityRatings: StateFlow<Map<Int, Double>> = _communityRatings.asStateFlow()

    // --- UI state ---
    private val _uiState = MutableStateFlow<LibraryUiState>(LibraryUiState.Idle)
    val uiState: StateFlow<LibraryUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            combine(watchlist, watched, favorites) { wl, wd, fav ->
                (wl + wd + fav).map { it.id }.distinct().sorted()
            }.distinctUntilChanged().collect { ids ->
                if (ids.isNotEmpty()) {
                    val docIds = ids.map { "MOVIE_$it" }
                    val summaries = reviewRepository.getContentSummaries(docIds)
                    _communityRatings.value = summaries.mapNotNull { (key, summary) ->
                        if (summary.avgRating > 0.0) key.removePrefix("MOVIE_").toIntOrNull()?.let { id -> id to summary.avgRating } else null
                    }.toMap()
                }
            }
        }
    }

    // --- Tab selection ---
    fun selectTab(tab: LibraryTab) {
        _selectedTab.value = tab
    }

    // --- Watchlist actions ---
    fun removeFromWatchlist(libraryMovie: LibraryMovie) {
        viewModelScope.launch {
            runCatching { repository.removeFromWatchlist(libraryMovie.id.toString()) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to remove from watchlist") }
        }
    }

    fun addToWatchlist(libraryMovie: LibraryMovie) {
        viewModelScope.launch {
            runCatching { repository.addToWatchlist(libraryMovie) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to add to watchlist") }
        }
    }

    // --- Watched actions ---
    fun markAsWatched(libraryMovie: LibraryMovie) {
        viewModelScope.launch {
            runCatching {
                repository.addToWatched(libraryMovie)
                repository.removeFromWatchlist(libraryMovie.id.toString())
            }.onFailure { _uiState.value = LibraryUiState.Error("Failed to mark as watched") }
        }
    }

    fun removeFromWatched(libraryMovie: LibraryMovie) {
        viewModelScope.launch {
            runCatching { repository.removeFromWatched(libraryMovie.id.toString()) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to remove from watched") }
        }
    }

    // --- Favorites actions ---
    fun addToFavorites(libraryMovie: LibraryMovie) {
        viewModelScope.launch {
            runCatching { repository.addToFavorites(libraryMovie) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to add to favorites") }
        }
    }

    fun removeFromFavorites(libraryMovie: LibraryMovie) {
        viewModelScope.launch {
            runCatching { repository.removeFromFavorites(libraryMovie.id.toString()) }
                .onFailure { _uiState.value = LibraryUiState.Error("Failed to remove from favorites") }
        }
    }

    fun clearError() {
        _uiState.value = LibraryUiState.Idle
    }
}
